<template>
  <div class="ga-admin-page">
    <AdminHeader
      :title="$t('ga_admin_execution_title')"
      :subtitle="$t('ga_admin_execution_subtitle')"
      compact
    />

    <div class="page-body">
      <div v-if="loading" class="loading-card">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ $t('ga_admin_loading_execution') }}</div>
      </div>
      <div v-else-if="errorMessage" class="state-card warning">{{ errorMessage }}</div>
      <template v-else>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_execution_section_overview') }}</div>
              <div class="section-desc">{{ $t('ga_admin_execution_section_overview_desc') }}</div>
            </div>
          </div>
          <div class="stats-grid">
            <div v-for="item in executionCards" :key="item.title" class="stat-card">
              <div class="stat-title">{{ item.title }}</div>
              <div class="stat-value">{{ item.value }}</div>
              <div :class="['stat-subvalue', item.tone]">{{ item.subValue }}</div>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_execution_section_monthly_visits') }}</div>
              <div class="section-desc">{{ $t('ga_admin_execution_section_monthly_visits_desc') }}</div>
            </div>
          </div>
          <div class="monthly-chart">
            <div class="chart-legend">
              <span class="legend-item"><i class="legend-dot gba"></i>GBA</span>
              <span class="legend-item"><i class="legend-dot ga"></i>GA</span>
            </div>
            <div class="bar-chart">
              <div v-for="item in monthlyVisitChart" :key="item.month" class="month-group">
                <div class="bar-area">
                  <div class="bar-column">
                    <span class="bar-value">{{ item.gba || '' }}</span>
                    <div class="bar gba" :style="{ height: getBarHeight(item.gba) }"></div>
                  </div>
                  <div class="bar-column">
                    <span class="bar-value">{{ item.ga || '' }}</span>
                    <div class="bar ga" :style="{ height: getBarHeight(item.ga) }"></div>
                  </div>
                </div>
                <div class="month-label">{{ item.label }}</div>
              </div>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_execution_section_monthly_feedback') }}</div>
              <div class="section-desc">{{ $t('ga_admin_execution_section_monthly_feedback_desc') }}</div>
            </div>
          </div>
          <div class="monthly-chart">
            <div class="chart-legend">
              <span class="legend-item"><i class="legend-dot gba"></i>GBA</span>
              <span class="legend-item"><i class="legend-dot ga"></i>GA</span>
            </div>
            <div class="bar-chart">
              <div v-for="item in monthlyFeedbackChart" :key="item.month" class="month-group">
                <div class="bar-area">
                  <div class="bar-column">
                    <span class="bar-value">{{ item.gba || '' }}</span>
                    <div class="bar gba" :style="{ height: getFeedbackBarHeight(item.gba) }"></div>
                  </div>
                  <div class="bar-column">
                    <span class="bar-value">{{ item.ga || '' }}</span>
                    <div class="bar ga" :style="{ height: getFeedbackBarHeight(item.ga) }"></div>
                  </div>
                </div>
                <div class="month-label">{{ item.label }}</div>
              </div>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_execution_section_pipeline') }}</div>
              <div class="section-desc">{{ $t('ga_admin_execution_section_pipeline_desc') }}</div>
            </div>
          </div>
          <div class="pipeline-grid">
            <div class="pipeline-card">
              <div class="pipeline-title">{{ $t('ga_admin_team_gba') }}</div>
              <div v-for="item in executionPipelines.gba" :key="item.stage" class="pipeline-row">
                <span>{{ item.stage }}</span>
                <span class="pipeline-count">{{ item.count }}</span>
              </div>
            </div>
            <div class="pipeline-card">
              <div class="pipeline-title">{{ $t('ga_admin_team_ga') }}</div>
              <div v-for="item in executionPipelines.ga" :key="item.stage" class="pipeline-row">
                <span>{{ item.stage }}</span>
                <span class="pipeline-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="rule-note">
            <div v-for="rule in positiveFeedbackRules" :key="rule" class="rule-row">{{ rule }}</div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_execution_section_queue') }}</div>
              <div class="section-desc">{{ $t('ga_admin_execution_section_queue_desc') }}</div>
            </div>
            <button class="ghost-btn ai-action-btn" @click="openAi($t('ga_admin_ai_prompt_queue'))">
              <font-awesome-icon :icon="['far', 'lightbulb']" class="ai-btn-icon" />
              <span>{{ $t('ga_admin_generate_priority') }}</span>
            </button>
          </div>
          <div class="queue-list">
            <div v-for="item in collaborationQueue" :key="item.title" class="queue-item">
              <div class="queue-top">
                <div class="queue-title">{{ item.title }}</div>
                <span class="status-pill">{{ item.status }}</span>
              </div>
              <div class="queue-desc">{{ item.desc }}</div>
              <div class="queue-owner">{{ $t('ga_admin_owner_team') }}：{{ item.owners }}</div>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_execution_section_quality_alert') }}</div>
              <div class="section-desc">{{ $t('ga_admin_execution_section_quality_alert_desc') }}</div>
            </div>
          </div>
          <div class="alert-list">
            <div
              v-for="item in qualityAlerts"
              :key="item.id || item.message"
              :class="['alert-item', { clickable: item.details && item.details.length }]"
              @click="toggleAlert(item)"
            >
              <div class="alert-main">
                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="alert-icon" />
                <span class="alert-message">{{ item.message }}</span>
                <span v-if="item.details && item.details.length" class="alert-count">
                  {{
                    expandedAlertId === item.id
                      ? $t('ga_admin_alert_collapse')
                      : $t('ga_admin_alert_view_detail', { type: item.detailType || $t('ga_admin_alert_detail') })
                  }}
                </span>
              </div>
              <div v-if="expandedAlertId === item.id && item.details && item.details.length" class="alert-detail">
                <div class="alert-detail-title">{{ item.detailTitle }}（{{ item.totalCount }}）</div>
                <div class="alert-detail-list">
                  <div v-for="detail in item.details" :key="detail.primary + detail.meta + detail.secondary" class="alert-detail-row">
                    <div class="detail-primary">{{ detail.primary }}</div>
                    <div v-if="detail.secondary" class="detail-secondary">{{ detail.secondary }}</div>
                    <div v-if="detail.meta" class="detail-meta">{{ detail.meta }}</div>
                  </div>
                </div>
                <div v-if="item.hiddenCount" class="alert-hidden-count">
                  {{ $t('ga_admin_alert_hidden_count', { count: item.hiddenCount }) }}
                </div>
              </div>
            </div>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import AdminHeader from './components/AdminHeader.vue';
import {loadGaAdminAnalytics} from './analytics';

export default {
  name: 'GAAdminExecution',
  components: {
    AdminHeader,
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      analytics: {
        executionCards: [],
        executionPipelines: {
          gba: [],
          ga: [],
        },
        monthlyVisitChart: [],
        monthlyFeedbackChart: [],
        positiveFeedbackRules: [],
        collaborationQueue: [],
        qualityAlerts: [],
      },
      expandedAlertId: '',
    };
  },
  computed: {
    executionCards() {
      return this.analytics.executionCards;
    },
    executionPipelines() {
      return this.analytics.executionPipelines;
    },
    monthlyVisitChart() {
      return this.analytics.monthlyVisitChart;
    },
    monthlyFeedbackChart() {
      return this.analytics.monthlyFeedbackChart;
    },
    maxMonthlyVisitCount() {
      return Math.max(...this.monthlyVisitChart.map(item => Math.max(item.gba, item.ga)), 1);
    },
    maxMonthlyFeedbackCount() {
      return Math.max(...this.monthlyFeedbackChart.map(item => Math.max(item.gba, item.ga)), 1);
    },
    positiveFeedbackRules() {
      return this.analytics.positiveFeedbackRules;
    },
    collaborationQueue() {
      return this.analytics.collaborationQueue;
    },
    qualityAlerts() {
      return this.analytics.qualityAlerts;
    },
  },
  watch: {
    '$i18n.locale'() {
      this.fetchAnalytics();
    },
  },
  created() {
    this.fetchAnalytics();
  },
  methods: {
    async fetchAnalytics() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const analytics = await loadGaAdminAnalytics();
        this.analytics = {
          ...this.analytics,
          ...analytics,
        };
      } catch (e) {
        console.error(e);
        this.errorMessage = this.$t('ga_admin_error_execution');
      } finally {
        this.loading = false;
      }
    },
    openAi(message) {
      this.$root.$emit('open-ai-chat', message, true, 'SRCopilotInsightGBA');
    },
    getBarHeight(value) {
      const percent = Math.max(Math.round((value / this.maxMonthlyVisitCount) * 100), value ? 8 : 0);
      return `${percent}%`;
    },
    getFeedbackBarHeight(value) {
      const percent = Math.max(Math.round((value / this.maxMonthlyFeedbackCount) * 100), value ? 8 : 0);
      return `${percent}%`;
    },
    toggleAlert(item) {
      if (!item.details || !item.details.length) return;
      this.expandedAlertId = this.expandedAlertId === item.id ? '' : item.id;
    },
  },
};
</script>

<style scoped lang="scss">
.ga-admin-page {
  min-height: 100%;
  background-color: #031b13;
  background:
    linear-gradient(122deg, transparent 0 16%, rgba(18, 83, 53, 0.56) 16% 29%, transparent 29% 100%),
    linear-gradient(122deg, transparent 0 60%, rgba(10, 58, 38, 0.72) 60% 76%, transparent 76% 100%),
    linear-gradient(180deg, #064527 0%, #003d25 34%, #031b13 100%);
}

.page-body {
  padding: 12px 12px 90px;
}

.state-card {
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.state-card.warning {
  color: #00b852;
}

.loading-card {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-radius: 16px;
  background: #fff;
  color: #666;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #f3d8d2;
  border-top-color: #00b852;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.section-card,
.stat-card,
.pipeline-card,
.queue-item {
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-card {
  margin-bottom: 12px;
  padding: 15px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #333;
}

.section-desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
}

.ghost-btn {
  border: 1.5px solid #00b852;
  border-radius: 999px;
  padding: 8px 12px;
  background: #fff;
  color: #00b852;
  font-size: 12px;
}

.ai-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  background: linear-gradient(135deg, #006b4f 0%, #008f72 48%, #00b852 100%);
  color: #fff;
  box-shadow: 0 6px 14px rgba(0, 184, 82, 0.2);
}

.ai-btn-icon {
  font-size: 13px;
}

.stats-grid,
.pipeline-grid {
  display: grid;
  gap: 12px;
}

.stats-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stat-card,
.pipeline-card,
.queue-item {
  padding: 14px;
}

.stat-title,
.pipeline-title {
  font-size: 13px;
  color: #666;
}

.stat-value {
  margin-top: 8px;
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.stat-subvalue {
  margin-top: 8px;
  font-size: 12px;
}

.stat-subvalue.positive {
  color: #0e9f6e;
}

.stat-subvalue.neutral {
  color: #1f3c88;
}

.stat-subvalue.warning {
  color: #00b852;
}

.pipeline-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.pipeline-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f8fbf9;
  font-size: 12px;
  color: #666;
}

.pipeline-count {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.rule-note {
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #f8fbf9;
}

.rule-row {
  font-size: 11px;
  line-height: 1.6;
  color: #7a4d42;
}

.rule-row + .rule-row {
  margin-top: 6px;
}

.monthly-chart {
  min-width: 0;
}

.chart-legend {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 11px;
  color: #666;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.legend-dot.gba,
.bar.gba {
  background: #00b852;
}

.legend-dot.ga,
.bar.ga {
  background: #1f3c88;
}

.bar-chart {
  width: 100%;
  box-sizing: border-box;
  height: 190px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(34px, 1fr));
  gap: 6px;
  align-items: end;
  padding: 8px 2px 0;
  border-radius: 14px;
  background:
    linear-gradient(to top, rgba(0, 184, 82, 0.08) 1px, transparent 1px) 0 0 / 100% 25%,
    #fbfaf9;
}

.month-group {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.bar-area {
  height: 148px;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 3px;
}

.bar-column {
  height: 100%;
  min-width: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
}

.bar-value {
  min-height: 14px;
  margin-bottom: 4px;
  font-size: 10px;
  line-height: 14px;
  color: #666;
}

.bar {
  width: 12px;
  min-height: 0;
  border-radius: 6px 6px 0 0;
  transition: height 0.2s ease;
}

.bar.ga {
  opacity: 0.92;
}

.month-label {
  margin-top: 8px;
  font-size: 11px;
  color: #666;
}

.queue-list,
.alert-list {
  display: grid;
  gap: 12px;
}

.queue-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.queue-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.status-pill {
  flex-shrink: 0;
  padding: 5px 10px;
  border-radius: 999px;
  background: #e5f8ed;
  color: #00b852;
  font-size: 11px;
}

.queue-desc,
.queue-owner {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.6;
  color: #666;
}

.alert-item {
  padding: 12px 14px;
  border-radius: 14px;
  background: #f1fff7;
  color: #7a4d42;
  font-size: 12px;
  line-height: 1.6;
}

.alert-item.clickable {
  cursor: pointer;
}

.alert-main {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.alert-message {
  flex: 1;
  min-width: 0;
}

.alert-count {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff;
  color: #00b852;
  font-size: 11px;
  font-weight: 700;
}

.alert-icon {
  margin-top: 2px;
  color: #00b852;
}

.alert-detail {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 184, 82, 0.14);
}

.alert-detail-title {
  font-size: 12px;
  font-weight: 700;
  color: #333;
}

.alert-detail-list {
  max-height: 280px;
  overflow-y: auto;
  display: grid;
  gap: 8px;
  margin-top: 8px;
}

.alert-detail-row {
  padding: 9px 10px;
  border-radius: 10px;
  background: #fff;
}

.detail-primary {
  font-size: 13px;
  font-weight: 700;
  color: #333;
}

.detail-secondary,
.detail-meta {
  margin-top: 3px;
  font-size: 11px;
  line-height: 1.5;
  color: #777;
  word-break: break-word;
}

.detail-meta {
  color: #a06658;
}

.alert-hidden-count {
  margin-top: 8px;
  font-size: 11px;
  color: #a06658;
}

@media (max-width: 360px) {
  .stats-grid,
  .pipeline-grid {
    grid-template-columns: 1fr;
  }
}
</style>
